import React from "react";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import {
  PASSWORD_INPUT_TOGGLE_CLASS,
  PASSWORD_TOGGLE_BTN_CLASS,
  showsPasswordToggle,
} from "../../lib/passwordFieldToggle";

/** Shared login fields — layout shells pass className overrides for visual variants. */
export function LoginFormFields({
  auth,
  inputClassName = "t-input",
  passwordInputClassName = PASSWORD_INPUT_TOGGLE_CLASS,
  passwordToggleBtnClassName = PASSWORD_TOGGLE_BTN_CLASS,
  primaryBtnClassName = "t-btn-primary",
  secondaryBtnClassName = "t-btn-primary",
  googleFirst = true,
  testIdPrefix = "login",
}) {
  const {
    email,
    password,
    showPassword,
    setShowPassword,
    busy,
    googleBusy,
    pwBusy,
    accessDenied,
    needs2fa,
    twoFaCode,
    setTwoFaCode,
    twoFaBusy,
    passwordToggleVisible,
    startGoogle,
    handleSubmit,
    passwordSignIn,
    complete2fa,
    onEmailChange,
    onPasswordChange,
  } = auth;

  if (needs2fa) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          complete2fa();
        }}
        data-testid={`${testIdPrefix}-2fa-form`}
      >
        <div className="label-eyebrow mb-2">Two-factor</div>
        <h2 className="font-heading mb-2 text-2xl tracking-tight">Check your email.</h2>
        <p className="mb-4 text-sm text-[var(--t-meta)]">Enter the 6-digit code we sent to finish signing in.</p>
        <label className="label-eyebrow mb-1 block">Verification code</label>
        <input
          className={`${inputClassName} mb-4`}
          inputMode="numeric"
          autoComplete="one-time-code"
          value={twoFaCode}
          onChange={(e) => setTwoFaCode(e.target.value)}
          data-testid={`${testIdPrefix}-2fa-code`}
        />
        <button
          type="submit"
          className={`${primaryBtnClassName} w-full flex items-center justify-center gap-2`}
          disabled={twoFaBusy || !(twoFaCode || "").trim()}
          data-testid={`${testIdPrefix}-2fa-submit`}
        >
          {twoFaBusy ? "Verifying…" : <>Continue <ArrowRight size={16} strokeWidth={1.4} /></>}
        </button>
      </form>
    );
  }

  const googleBtn = (
    <button
      type="button"
      className={`${secondaryBtnClassName} mb-2 flex w-full items-center justify-center gap-2`}
      onClick={startGoogle}
      disabled={googleBusy}
      data-testid={`${testIdPrefix}-google-btn`}
    >
      {googleBusy ? "Signing in…" : <>Sign in w/ Google <ArrowRight size={16} strokeWidth={1.4} /></>}
    </button>
  );

  const magicBtn = (
    <button
      type="submit"
      className={`${primaryBtnClassName} w-full flex items-center justify-center gap-2`}
      disabled={busy}
      data-testid={`${testIdPrefix}-submit-btn`}
    >
      {busy ? "Sending…" : <>Send magic link <ArrowRight size={16} strokeWidth={1.4} /></>}
    </button>
  );

  return (
    <form onSubmit={handleSubmit} data-testid={`${testIdPrefix}-form`}>
      <label className="label-eyebrow mb-1 block">Email</label>
      <input
        type="email"
        className={`${inputClassName} mb-4`}
        placeholder="you@studio.com"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        required
        data-testid={`${testIdPrefix}-email-input`}
      />
      <div className="mb-2 flex items-center justify-between">
        <label className="label-eyebrow block">Password</label>
        {accessDenied ? (
          <span
            className="font-heading text-xs tracking-[0.2em] text-[var(--t-accent)]"
            data-testid={`${testIdPrefix}-access-denied`}
          >
            ACCESS DENIED
          </span>
        ) : (
          (password || "").trim().length >= 3 && (
            <button
              type="button"
              onClick={passwordSignIn}
              disabled={pwBusy}
              className="cursor-pointer text-xs uppercase tracking-widest text-[var(--t-accent)] hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid={`${testIdPrefix}-password-signin`}
            >
              {pwBusy ? "Signing in…" : "Sign in"}
            </button>
          )
        )}
      </div>
      <div className="relative mb-4">
        <input
          type={showPassword ? "text" : "password"}
          className={passwordInputClassName}
          placeholder="••••••••"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="current-password"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              passwordSignIn();
            }
          }}
          data-testid={`${testIdPrefix}-password-input`}
        />
        {showsPasswordToggle(password) ? (
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className={`${passwordToggleBtnClassName} transition-opacity duration-300 ${
              passwordToggleVisible ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-label={showPassword ? "Hide password" : "Show password"}
            data-testid={
              testIdPrefix === "login" ? "toggle-password-visibility" : `${testIdPrefix}-toggle-password-visibility`
            }
          >
            {showPassword ? <EyeOff size={16} strokeWidth={1.6} /> : <Eye size={16} strokeWidth={1.6} />}
          </button>
        ) : null}
      </div>
      {googleFirst ? (
        <>
          {googleBtn}
          {magicBtn}
        </>
      ) : (
        <>
          {magicBtn}
          <div className="mt-2">{googleBtn}</div>
        </>
      )}
    </form>
  );
}

export function LoginSentPanel({ auth, testIdPrefix = "login", backClassName = "t-btn-secondary w-full" }) {
  const { email, resetSent } = auth;
  return (
    <div data-testid={`${testIdPrefix}-sent-screen`}>
      <div className="label-eyebrow mb-2">Check your inbox</div>
      <h2 className="font-heading mb-1 text-2xl tracking-tight md:text-3xl">Link sent.</h2>
      <p className="mb-5 text-sm leading-snug text-[var(--t-meta)] md:mb-6 md:leading-relaxed">
        If <span className="text-[var(--t-text)]">{email}</span> is registered, a link is on its way. The link expires in 24 hours.
      </p>
      <button onClick={resetSent} className={backClassName} data-testid={`${testIdPrefix}-back-btn`}>
        Use a different email
      </button>
    </div>
  );
}
